import React, { useEffect, useState } from "react";
import { Bell, Clock, Calendar, Sparkles, Plus, X } from "lucide-react";
import { api, formatErr } from "../lib/api";

/**
 * ReminderCadenceCard — gestione cadenza dei promemoria:
 * - Cliente: 4 preset + custom hours (max 4)
 * - Owner: daily summary + lead-time per appuntamento
 */
export default function ReminderCadenceCard({ businessId }) {
  const [form, setForm] = useState({
    customer_reminder_hours: [24, 2],
    owner_daily_summary_enabled: true,
    owner_daily_summary_hour: 8,
    owner_lead_time_min: null,
  });
  const [customMode, setCustomMode] = useState(false);
  const [customDraft, setCustomDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/businesses/${businessId}/reminder-settings`)
      .then((r) => {
        const data = r.data;
        setForm(data);
        // Auto-detect "custom" if it doesn't match any preset
        const k = JSON.stringify([...data.customer_reminder_hours].sort((a, b) => b - a));
        const presetKeys = ["[]", "[2]", "[24]", "[24,2]"];
        if (!presetKeys.includes(k)) setCustomMode(true);
      })
      .finally(() => setLoading(false));
  }, [businessId]);

  function setPreset(hours) {
    setCustomMode(false);
    setForm((f) => ({ ...f, customer_reminder_hours: hours }));
  }

  function presetActive(hours) {
    if (customMode) return false;
    const a = [...form.customer_reminder_hours].sort((x, y) => y - x).join(",");
    const b = [...hours].sort((x, y) => y - x).join(",");
    return a === b;
  }

  function addCustomHour() {
    const n = parseInt(customDraft, 10);
    if (isNaN(n) || n < 1 || n > 168) {
      setErr("Inserisci un numero tra 1 e 168 ore");
      return;
    }
    if (form.customer_reminder_hours.includes(n)) {
      setErr("Questa ora è già stata aggiunta");
      return;
    }
    if (form.customer_reminder_hours.length >= 4) {
      setErr("Massimo 4 promemoria");
      return;
    }
    setErr("");
    setForm((f) => ({
      ...f,
      customer_reminder_hours: [...f.customer_reminder_hours, n].sort((a, b) => b - a),
    }));
    setCustomDraft("");
  }

  function removeCustomHour(h) {
    setForm((f) => ({
      ...f,
      customer_reminder_hours: f.customer_reminder_hours.filter((x) => x !== h),
    }));
  }

  async function save() {
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.put(`/businesses/${businessId}/reminder-settings`, {
        customer_reminder_hours: form.customer_reminder_hours,
        owner_daily_summary_enabled: form.owner_daily_summary_enabled,
        owner_daily_summary_hour: Number(form.owner_daily_summary_hour),
        owner_lead_time_min: form.owner_lead_time_min,
      });
      setForm(data);
      setSavedAt(new Date());
      setTimeout(() => setSavedAt(null), 3000);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="card-soft p-4 text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>;
  }

  return (
    <div className="card-soft p-5" data-testid="reminder-cadence-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-zinc-900 text-white flex items-center justify-center flex-shrink-0">
          <Bell className="h-5 w-5" />
        </div>
        <div>
          <div className="font-medium">Cadenza promemoria</div>
          <p className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">Quando inviare i promemoria automatici a te e ai tuoi clienti.</p>
        </div>
      </div>

      {/* === CLIENTI === */}
      <section className="mt-4">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="h-4 w-4 text-blue-600" />
          <h4 className="font-medium text-sm">Promemoria al cliente</h4>
        </div>
        <p className="text-xs text-zinc-500 mb-3 dark:text-zinc-400">
          Email + push automatici alle ore selezionate prima dell'appuntamento. I clienti senza email ricevono solo push.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { key: "none", label: "Disattivati", hours: [] },
            { key: "2h", label: "Solo 2h prima", hours: [2] },
            { key: "24h", label: "Solo 24h prima", hours: [24] },
            { key: "both", label: "24h + 2h prima", hours: [24, 2] },
          ].map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPreset(p.hours)}
              className={`text-left rounded-xl border p-3 transition ${presetActive(p.hours) ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 hover:border-zinc-400"}`}
              data-testid={`reminder-preset-${p.key}`}
            >
              <div className="text-sm font-medium">{p.label}</div>
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setCustomMode((v) => !v)}
          className="mt-3 text-xs font-medium text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 dark:text-blue-300"
          data-testid="reminder-custom-toggle"
        >
          <Sparkles className="h-3 w-3" />
          {customMode ? "Nascondi personalizzazione" : "Personalizza orari"}
        </button>

        {customMode && (
          <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-3 dark:border-blue-800/50 dark:bg-blue-950/40" data-testid="reminder-custom-area">
            <div className="text-xs text-blue-900 mb-2 dark:text-blue-200">
              Inserisci fino a 4 valori in ore (1-168). Es: 48 = 2 giorni prima.
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {form.customer_reminder_hours.map((h) => (
                <span key={h} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-blue-300 text-xs font-medium dark:bg-zinc-900" data-testid={`reminder-hour-${h}`}>
                  {h >= 24 ? `${h / 24}gg` : `${h}h`}
                  <button type="button" onClick={() => removeCustomHour(h)} className="text-blue-600 hover:text-blue-900 dark:text-blue-200">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {form.customer_reminder_hours.length === 0 && (
                <span className="text-xs text-blue-700 italic dark:text-blue-300">Nessun promemoria configurato</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={168}
                placeholder="Es. 48"
                className="input-soft h-9 text-sm flex-1"
                value={customDraft}
                onChange={(e) => setCustomDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomHour(); } }}
                data-testid="reminder-custom-input"
                disabled={form.customer_reminder_hours.length >= 4}
              />
              <span className="text-xs text-blue-900 dark:text-blue-200">ore</span>
              <button
                type="button"
                onClick={addCustomHour}
                disabled={form.customer_reminder_hours.length >= 4}
                className="h-9 px-3 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="reminder-custom-add"
              >
                <Plus className="h-3 w-3" />Aggiungi
              </button>
            </div>
          </div>
        )}
      </section>

      {/* === OWNER === */}
      <section className="mt-6 pt-5 border-t border-zinc-100 dark:border-zinc-800">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4 text-emerald-600" />
          <h4 className="font-medium text-sm">Promemoria per te</h4>
        </div>

        {/* Daily summary */}
        <div className="rounded-xl border border-zinc-200 p-3 mb-3 dark:border-zinc-700" data-testid="reminder-daily-block">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded"
              checked={form.owner_daily_summary_enabled}
              onChange={(e) => setForm((f) => ({ ...f, owner_daily_summary_enabled: e.target.checked }))}
              data-testid="reminder-daily-enabled"
            />
            <div className="flex-1">
              <div className="text-sm font-medium">Riepilogo giornaliero</div>
              <div className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">
                Push al mattino con il numero di appuntamenti del giorno e l'orario del primo.
              </div>
              {form.owner_daily_summary_enabled && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-zinc-600 dark:text-zinc-400">Invia alle</span>
                  <select
                    className="input-soft h-8 text-sm w-auto"
                    value={form.owner_daily_summary_hour}
                    onChange={(e) => setForm((f) => ({ ...f, owner_daily_summary_hour: Number(e.target.value) }))}
                    data-testid="reminder-daily-hour"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 6).map((h) => (
                      <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </label>
        </div>

        {/* Lead-time per booking */}
        <div className="rounded-xl border border-zinc-200 p-3 dark:border-zinc-700" data-testid="reminder-leadtime-block">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded"
              checked={form.owner_lead_time_min !== null}
              onChange={(e) => setForm((f) => ({ ...f, owner_lead_time_min: e.target.checked ? 15 : null }))}
              data-testid="reminder-leadtime-enabled"
            />
            <div className="flex-1">
              <div className="text-sm font-medium">Promemoria prima di ogni appuntamento</div>
              <div className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">
                Push X minuti prima dell'inizio di ogni appuntamento (anche se sei in giro).
              </div>
              {form.owner_lead_time_min !== null && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-zinc-600 dark:text-zinc-400">Avvisami</span>
                  <select
                    className="input-soft h-8 text-sm w-auto"
                    value={form.owner_lead_time_min}
                    onChange={(e) => setForm((f) => ({ ...f, owner_lead_time_min: Number(e.target.value) }))}
                    data-testid="reminder-leadtime-value"
                  >
                    <option value={5}>5 minuti prima</option>
                    <option value={15}>15 minuti prima</option>
                    <option value={30}>30 minuti prima</option>
                    <option value={60}>1 ora prima</option>
                    <option value={120}>2 ore prima</option>
                  </select>
                </div>
              )}
            </div>
          </label>
        </div>
      </section>

      {/* SAVE */}
      <div className="mt-5 pt-4 border-t border-zinc-100 flex items-center justify-between gap-3 dark:border-zinc-800">
        {err ? (
          <div className="text-sm text-red-600 flex-1">{err}</div>
        ) : savedAt ? (
          <div className="text-xs text-emerald-600 flex-1">✓ Salvato</div>
        ) : (
          <div className="text-xs text-zinc-400 flex-1 dark:text-zinc-500">Le modifiche si applicano agli appuntamenti futuri.</div>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-accent disabled:opacity-50"
          data-testid="reminder-save"
        >
          {saving ? "Salvataggio…" : "Salva"}
        </button>
      </div>
    </div>
  );
}
